import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import { useAssets } from '@labradorsports/assets';
import { ButtonsBar, StyledTextField, ConfirmDeleteModal } from '@labradorsports/components';
import { isValidScreenName, useSite } from '@labradorsports/utils';

import { ProfileImageEditor } from '../../profile/index.js';
import { useDispatch } from '../../shared/hooks/index.js';
import { HelmetWrapper, addValidation, useDispatcher, useValidation } from '../../shared/index.js';
import { useNavigateWithQuery } from '../../shared/providers/index.js';
import {
    communityApi,
    routerActions,
    useGetCurrentCommunityProfileQuery,
    useSetupCommunityProfileMutation,
    useUnshareCommunityMutation,
} from '../../store/index.js';
import { RootState, RouterSelectors, Selectors } from '../../store/state.js';
import CommunityPlayList from '../community-play-list/community-play-list.js';
import CommunityProfileSetup from '../community-profile-setup/community-profile-setup.js';

interface Props {
    addPlay: AnyFunction;
}

const CommunityProfile: FC<Props> = ({ addPlay }) => {
    const dispatch = useDispatch();
    const { Config } = useSite();
    const { AddFrameWhiteIcon } = useAssets();
    const navigate = useNavigateWithQuery();
    const [setupCommunityProfile] = useSetupCommunityProfileMutation();
    const setCommunityProfileId = useDispatcher(routerActions.SetCommunityProfileId);
    const [unshareCommunity] = useUnshareCommunityMutation();
    const communityProfileId = useSelector(RouterSelectors.communityProfileId);
    const user = useSelector(Selectors.user);
    const authReady = useSelector((state: RootState) => state.auth.authReady);
    const { data: communityProfile } = useGetCurrentCommunityProfileQuery();

    const [editing, setEditing] = useState(false);
    const [screenName, setScreenName] = useState('');
    const [description, setDescription] = useState('');
    const [confirmRemovePlay, setConfirmRemovePlay] = useState(null);

    const { errors, submitted, setSubmitted, validateRef } = useValidation(async () => {
        const result =
            screenName !== ''
                ? await dispatch(communityApi.endpoints.getCommunityProfile.initiate({ screenName }))
                : null;

        return {
            screenNameInvalid: !isValidScreenName(screenName),
            screenNameMissing: screenName === '',
            screenNameTaken: result?.data && result.data.uid !== user.uid,
        };
    }, [screenName]);

    useEffect(() => {
        if (!communityProfileId) {
            setCommunityProfileId('me');
        } else if (communityProfileId === 'me' && authReady && !user) {
            navigate('/community');
        }
    }, [communityProfileId, authReady, user]);

    const isMyProfile = communityProfileId === 'me';

    const screenNameChange = (evt) => {
        setScreenName(evt.target.value);
    };

    const descriptionChange = (evt) => {
        setDescription(evt.target.value);
    };

    const updateClick = () => {
        setEditing(true);
        setScreenName(communityProfile.screenName);
        setDescription(communityProfile.description);
    };

    const onCancel = () => {
        setEditing(false);
    };

    const onSubmit = async () => {
        if (await validateRef.current()) {
            await setupCommunityProfile({ screenName, description });
            onCancel();
        } else {
            setSubmitted(true);
        }
    };

    const screenNameError = (() => {
        if (errors.screenNameMissing) {
            return 'Please enter a screen name';
        }

        if (errors.screenNameTaken) {
            return 'Screen name is already taken';
        }

        if (errors.screenNameInvalid) {
            return 'Screen name is invalid';
        }

        return null;
    })();

    const startJoinCommunity = () => {
        setEditing(true);
    };

    if (!communityProfile && editing) {
        return (
            <div className="community-profile">
                <CommunityProfileSetup onSuccess={onCancel} onCancel={onCancel} />;
            </div>
        );
    }

    const unsharePlay = async () => {
        await unshareCommunity({
            playId: confirmRemovePlay.playId,
            srcPlaybookId: confirmRemovePlay.playbookId,
            programId: confirmRemovePlay.programId,
        });

        setConfirmRemovePlay(null);
    };

    const canonical = `${Config.SiteConfig.Url}/community/profile?pr=${communityProfileId}`;

    return (
        <div className="community-profile p-3">
            {communityProfile && communityProfileId !== 'me' ? (
                <HelmetWrapper
                    title={`${communityProfileId} on ${Config.SiteConfig.SiteName}`}
                    description={communityProfile.description}
                    image={communityProfile.profileImageUrl}
                >
                    <link rel="canonical" href={canonical} />
                    <meta property="og:url" content={canonical} />
                </HelmetWrapper>
            ) : (
                <HelmetWrapper title="My Community Profile" />
            )}
            <ConfirmDeleteModal
                open={Boolean(confirmRemovePlay)}
                confirm={unsharePlay}
                cancel={() => setConfirmRemovePlay(null)}
                text={`Are you sure you want to remove ${confirmRemovePlay?.name} from the Community playbook?`}
            />

            {!communityProfile ? (
                <div className="alert alert-warning">
                    You must set up your public profile to share plays to the community
                    <Button onClick={startJoinCommunity}>Join Community</Button>
                </div>
            ) : null}
            {communityProfile ? (
                <div className="profile-details flex-grow-1">
                    <div className="d-sm-flex">
                        <ProfileImageEditor
                            readOnly={!isMyProfile}
                            profileImageUrl={communityProfile.profileImageUrl}
                        />
                        <div className="p-3 flex-grow-1">
                            {editing ? (
                                <StyledTextField
                                    label="Screen Name"
                                    value={screenName}
                                    onChange={screenNameChange}
                                    helperText={submitted && screenNameError}
                                    error={submitted && Boolean(screenNameError)}
                                    fullWidth
                                />
                            ) : (
                                <h4>{communityProfile.screenName}</h4>
                            )}

                            <a href={`https://${communityProfile.website}`} target="_blank" rel="noreferrer">
                                {communityProfile.website}
                            </a>
                            {editing ? (
                                <StyledTextField
                                    label="About Me"
                                    value={description}
                                    onChange={descriptionChange}
                                    multiline
                                    fullWidth
                                />
                            ) : (
                                <>
                                    <p>
                                        <em>About</em>
                                    </p>
                                    {communityProfile.description ? (
                                        <div>{communityProfile.description}</div>
                                    ) : (
                                        <div>
                                            <i className="small">No description added</i>
                                        </div>
                                    )}
                                </>
                            )}
                            {isMyProfile && !editing ? <Button onClick={updateClick}>Update</Button> : null}
                            {editing ? (
                                <ButtonsBar>
                                    <Button className="float-start" onClick={onCancel}>
                                        Cancel
                                    </Button>
                                    <Button variant="contained" color="primary" onClick={onSubmit}>
                                        Confirm
                                    </Button>
                                </ButtonsBar>
                            ) : null}
                        </div>
                        {isMyProfile ? (
                            <div className="flex-shrink-0">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className="rounded-button"
                                    onClick={addPlay}
                                >
                                    <img src={AddFrameWhiteIcon} alt="Add icon" />
                                    Add Plays
                                </Button>
                            </div>
                        ) : null}
                    </div>

                    <div className="mt-3">
                        <em>My Shared Plays</em>
                        {communityProfile.sharedPlays?.length > 0 ? (
                            <CommunityPlayList
                                plays={communityProfile.sharedPlays}
                                onRemove={isMyProfile ? setConfirmRemovePlay : null}
                            />
                        ) : (
                            <div>You have not shared any plays yet.</div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default addValidation(CommunityProfile);
